import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar, Clock, TrendingUp, Baby, AlertCircle } from 'lucide-react';
import { format, differenceInDays, isFuture, isPast } from 'date-fns';
import { calculateExpectedPeriodDates } from '@/utils/lunarPhases';

interface CyclePredictorProps {
  cycleStartDate: Date;
  cycleLength: number;
  periodEndDate?: Date;
  isPerimenopausal?: boolean;
  isMenopausal?: boolean;
  isPregnancyMode?: boolean;
  dueDate?: Date;
  onEnterPeriod?: () => void;
  onSwitchToPregnancy?: () => void;
}

export default function CyclePredictor({ 
  cycleStartDate, 
  cycleLength, 
  periodEndDate,
  isPerimenopausal = false,
  isMenopausal = false,
  isPregnancyMode = false,
  dueDate,
  onEnterPeriod,
  onSwitchToPregnancy
}: CyclePredictorProps) {
  const today = new Date();
  const daysSinceLastPeriod = differenceInDays(today, cycleStartDate);
  
  // For perimenopause, don't use fixed predictions - show ranges instead
  const predictedPeriods = !isPerimenopausal ? calculateExpectedPeriodDates(cycleStartDate, cycleLength) : [];
  
  // Find next period (only for regular cycles)
  const nextPeriod = predictedPeriods.find(date => isFuture(date));
  const daysUntilNext = nextPeriod ? differenceInDays(nextPeriod, today) : null;
  
  // Current cycle day (only relevant for regular cycles)
  const normalizedCycleDay = !isPerimenopausal ? ((daysSinceLastPeriod - 1) % cycleLength) + 1 : null;
  
  // Calculate if period is late
  const isPeriodLate = nextPeriod && isPast(nextPeriod);
  const daysLate = isPeriodLate ? Math.abs(daysUntilNext || 0) : 0;
  
  // Pregnancy mode calculations
  const daysToDueDate = dueDate ? differenceInDays(dueDate, today) : null;
  const weeksPregnant = dueDate ? Math.floor((280 - (daysToDueDate || 0)) / 7) : null;

  // Pregnancy Mode View
  if (isPregnancyMode && dueDate) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Baby className="h-5 w-5" />
            Pregnancy Journey
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-center p-6 bg-gradient-to-br from-pink-500/10 to-purple-500/10 rounded-lg border border-pink-500/20">
            <div className="text-4xl font-bold text-primary mb-2">{daysToDueDate}</div>
            <div className="text-sm text-muted-foreground mb-1">Days Until Due Date</div>
            <div className="text-2xl font-semibold text-primary">{weeksPregnant} weeks</div>
          </div>
          
          <div className="p-4 border rounded-lg">
            <h4 className="font-medium mb-2">Expected Due Date</h4>
            <p className="text-lg font-semibold">{format(dueDate, 'EEEE, MMMM d, yyyy')}</p>
          </div>
          
          <div className="p-4 bg-amber-500/5 border border-amber-500/20 rounded-lg">
            <p className="text-sm text-muted-foreground">
              Period tracking is paused during pregnancy. Visit the Pregnancy & Postpartum section for comprehensive tracking.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5" />
          Cycle Predictions
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Late Period Alert */}
        {isPeriodLate && onEnterPeriod && (
          <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg space-y-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-medium text-red-700 dark:text-red-400">Period {daysLate} Day{daysLate > 1 ? 's' : ''} Late</h4>
                <p className="text-sm text-muted-foreground mt-1">
                  Your period was expected on {format(nextPeriod, 'MMM d')}. Has it started?
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={onEnterPeriod} size="sm" className="flex-1">
                Yes, Enter Period Start Date
              </Button>
              <Button 
                onClick={onSwitchToPregnancy} 
                variant="outline" 
                size="sm" 
                className="flex-1"
              >
                <Baby className="h-4 w-4 mr-1" />
                Could be Pregnant?
              </Button>
            </div>
          </div>
        )}
        
        {/* Current Status */}
        {isPerimenopausal ? (
          // Perimenopause: Show days since last period
          <div className="space-y-4">
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold text-primary">{daysSinceLastPeriod}</div>
              <div className="text-sm text-muted-foreground">Days Since Last Period</div>
            </div>
            
            <div className="p-4 border rounded-lg bg-amber-500/5 border-amber-500/20">
              <h4 className="font-medium mb-2 text-amber-700 dark:text-amber-400">Perimenopause Pattern</h4>
              <p className="text-sm text-muted-foreground">
                Your cycles may vary from {Math.max(21, cycleLength - 14)} to {cycleLength + 21} days during this transition. 
                Focus on tracking symptoms and patterns rather than fixed predictions.
              </p>
            </div>
          </div>
        ) : (
          // Regular cycle: Show cycle day and prediction
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold text-primary">{normalizedCycleDay}</div>
              <div className="text-sm text-muted-foreground">Current Cycle Day</div>
            </div>
            
            {daysUntilNext && (
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <div className="text-2xl font-bold text-primary">{daysUntilNext}</div>
                <div className="text-sm text-muted-foreground">Days Until Next Period</div>
              </div>
            )}
          </div>
        )}

        {/* Next Period Prediction - Only for regular cycles */}
        {!isPerimenopausal && nextPeriod && (
          <div className="p-4 border rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Next Expected Period
              </h4>
              <Badge variant="outline">
                <Clock className="h-3 w-3 mr-1" />
                {daysUntilNext} days
              </Badge>
            </div>
            <p className="text-lg font-semibold">{format(nextPeriod, 'EEEE, MMMM d, yyyy')}</p>
            <p className="text-sm text-muted-foreground mt-1">
              Based on your {cycleLength}-day cycle pattern
            </p>
          </div>
        )}

        {/* Upcoming Periods - Only for regular cycles */}
        {!isPerimenopausal && predictedPeriods.length > 1 && (
          <div>
            <h4 className="font-medium mb-3">Next 3 Predicted Periods</h4>
            <div className="space-y-2">
              {predictedPeriods.slice(1, 4).map((date, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                  <span className="text-sm font-medium">
                    Period {index + 2}
                  </span>
                  <span className="text-sm">
                    {format(date, 'MMM d, yyyy')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Perimenopause guidance */}
        {isPerimenopausal && (
          <div className="p-4 border rounded-lg bg-gradient-to-br from-purple-500/5 to-pink-500/5">
            <h4 className="font-medium mb-2">Tracking Tips for Perimenopause</h4>
            <ul className="text-sm text-muted-foreground space-y-1.5 list-disc list-inside">
              <li>Track symptoms daily to identify your unique patterns</li>
              <li>Notice how symptoms align with lunar phases for deeper insights</li>
              <li>Expect cycle variations - this is completely normal</li>
              <li>Focus on self-care during unpredictable times</li>
            </ul>
          </div>
        )}

        {/* Cycle Summary */}
        <div className="p-3 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-lg">
          <h4 className="font-medium mb-2">{isPerimenopausal ? 'Tracking Summary' : 'Your Cycle Summary'}</h4>
          <div className="text-sm space-y-1">
            <p><strong>Last Period Started:</strong> {format(cycleStartDate, 'MMM d, yyyy')}</p>
            {periodEndDate && (
              <p><strong>Period Length:</strong> {differenceInDays(periodEndDate, cycleStartDate) + 1} days</p>
            )}
            {!isPerimenopausal && (
              <>
                <p><strong>Typical Cycle Length:</strong> {cycleLength} days</p>
                <p><strong>Current Day:</strong> Day {normalizedCycleDay} of {cycleLength}</p>
              </>
            )}
            {isPerimenopausal && (
              <p><strong>Days Since Period Started:</strong> {daysSinceLastPeriod} days</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}